import React, { Fragment } from 'react';

import { getBackgroundColor } from 'libs/styleHelpers';
import { centsToDollars } from 'libs/utils';

import { Product } from 'components/layout/PursuitApplication';
import { Loader } from 'components/ui/Loader';
import { SelectableCard } from 'components/ui/SelectableCard';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, HorizontalFlexWrapper } from 'styles/shared';

interface SelectProductProps {
    products: Product[];
    selectedProduct: Product | null;
    onSelectProduct: (product: Product) => () => void;
    isLoadingProducts: boolean;
    loadingSelectedProductId: string;
}

export const SelectProduct = ({
    products,
    selectedProduct,
    onSelectProduct,
    isLoadingProducts,
    loadingSelectedProductId,
}: SelectProductProps) =>
    isLoadingProducts ? (
        <>
            <Fonts.Medium>{`Loading products...`}</Fonts.Medium>
            <Bumper />
            <Loader $color={colors.grandGreen} />
        </>
    ) : (
        <div>
            {products.map((product, index) => (
                <Fragment key={product.id}>
                    <SelectableCard
                        title={product.name}
                        subtitle={product.metadata.tagline ?? ''}
                        onSelect={onSelectProduct(product)}
                        disabledText={product.metadata.soldOutText}
                        $isSelected={selectedProduct?.id === product.id}
                        $isLoading={loadingSelectedProductId === product.id}
                        $backgroundColor={getBackgroundColor(index)}
                    >
                        <Fonts.Small $textAlign="left">
                            {product.description}
                            {product.metadata.link == null ? null : (
                                <>
                                    {` `}
                                    <Fonts.Small
                                        as="a"
                                        href={product.metadata.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        $isUnderlined
                                    >{`Learn more`}</Fonts.Small>
                                </>
                            )}
                        </Fonts.Small>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <Bumper $minWidth={spacing.large} />
                            <Fonts.MediumQuote $textAlign="left">
                                {product.metadata.testimonial ?? ''}
                            </Fonts.MediumQuote>
                            <Bumper $minWidth={spacing.large} />
                        </HorizontalFlexWrapper>
                        <Bumper />
                        {product.prices.map((price) => (
                            <>
                                <Fonts.Medium key={price.id} $textAlign="left">
                                    {`${
                                        price.recurring?.interval === 'month'
                                            ? 'Monthly'
                                            : 'Annual'
                                    } subscription: `}
                                    <Fonts.Medium
                                        $isOpenSansSemiBold
                                    >{`${centsToDollars(
                                        price.unit_amount
                                    )}`}</Fonts.Medium>
                                </Fonts.Medium>
                                <Bumper $minHeight={spacing.xxxSmall} />
                            </>
                        ))}
                        {product.metadata.disclaimer == null ? null : (
                            <Fonts.XSmall>
                                {product.metadata.disclaimer}
                            </Fonts.XSmall>
                        )}
                        <Bumper />
                    </SelectableCard>
                    {index === products.length - 1 ? null : <Bumper />}
                </Fragment>
            ))}
        </div>
    );
